(() => {
  if (customElements.get('three-dio-mode-lightbox')) return;

  class ThreeDioModeLightbox extends HTMLElement {
    connectedCallback() {
      if (!this.movedToBody) {
        this.movedToBody = true;
        document.body.appendChild(this);
        return;
      }
      if (this.initialized) return;

      [...document.querySelectorAll('three-dio-mode-lightbox')]
        .filter((lightbox) => lightbox !== this && lightbox.dataset.sectionId === this.dataset.sectionId)
        .forEach((lightbox) => lightbox.remove());

      this.dialog = this.querySelector('[data-dio-lightbox-dialog]');
      this.slides = [...this.querySelectorAll('[data-dio-lightbox-slide]')];
      this.captions = [...this.querySelectorAll('[data-dio-lightbox-caption]')];
      this.details = this.querySelector('.dio-mode-lightbox__details');
      this.controls = this.querySelector('[data-dio-lightbox-controls]');
      this.previousButton = this.querySelector('[data-dio-lightbox-previous]');
      this.nextButton = this.querySelector('[data-dio-lightbox-next]');
      this.playbackButton = this.querySelector('[data-dio-lightbox-playback]');
      this.playIcon = this.querySelector('[data-dio-lightbox-play-icon]');
      this.pauseIcon = this.querySelector('[data-dio-lightbox-pause-icon]');
      this.currentOutput = this.querySelector('[data-dio-lightbox-current]');
      this.gallery = document.getElementById(this.dataset.galleryId);
      this.trigger = [...document.querySelectorAll('[data-dio-lightbox-trigger]')].find(
        (trigger) => trigger.dataset.dioLightboxTrigger === this.id
      );
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.loop = this.dataset.loop === 'true';
      this.autoplay = this.dataset.autoplay === 'true';
      this.activeIndex = 0;
      this.playing = false;
      this.isOpen = false;
      this.inertedElements = [];

      if (!this.dialog || !this.slides.length || !this.trigger) return;

      this.handleTrigger = this.handleTrigger.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleReducedMotion = this.handleReducedMotion.bind(this);
      this.handleGalleryChange = this.handleGalleryChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);

      this.trigger.addEventListener('click', this.handleTrigger);
      this.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('visibilitychange', this.handleVisibility);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
      window.addEventListener('pageshow', this.handlePageShow);
      this.gallery?.addEventListener('dio:mode-change', this.handleGalleryChange);
      this.reducedMotion.addEventListener?.('change', this.handleReducedMotion);

      this.initialized = true;
      this.setSlide(0);
      this.trigger.hidden = false;
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.clearTimer();
      this.clearCloseTimer();
      this.trigger?.removeEventListener('click', this.handleTrigger);
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      window.removeEventListener('pageshow', this.handlePageShow);
      this.gallery?.removeEventListener('dio:mode-change', this.handleGalleryChange);
      this.reducedMotion?.removeEventListener?.('change', this.handleReducedMotion);
      if (this.isOpen) this.finishClose(false);
      this.initialized = false;
    }

    handleTrigger(event) {
      event.preventDefault();
      this.open(this.trigger);
    }

    handleClick(event) {
      if (event.target.closest('[data-dio-lightbox-close]')) {
        this.close();
      } else if (event.target.closest('[data-dio-lightbox-previous]')) {
        this.move(-1);
      } else if (event.target.closest('[data-dio-lightbox-next]')) {
        this.move(1);
      } else if (event.target.closest('[data-dio-lightbox-playback]')) {
        if (this.playing) this.pausePlayback();
        else this.startPlayback();
      }
    }

    handleKeydown(event) {
      if (!this.isOpen || event.code.toUpperCase() !== 'ESCAPE') return;
      event.preventDefault();
      this.close();
    }

    handleVisibility() {
      if (!this.isOpen || !this.playing) return;
      if (document.hidden) this.clearTimer();
      else this.scheduleNext();
    }

    handleReducedMotion(event) {
      if (event.matches && this.isOpen) this.pausePlayback();
    }

    handleGalleryChange(event) {
      if (!this.isOpen || !event.detail?.blockId) return;
      const index = this.slides.findIndex((slide) => slide.dataset.dioBlockId === event.detail.blockId);
      if (index >= 0) this.setSlide(index, true);
    }

    handleBlockSelect(event) {
      if (!this.isOpen || !event.detail?.blockId) return;
      const index = this.slides.findIndex((slide) => slide.dataset.dioBlockId === String(event.detail.blockId));
      if (index >= 0) this.setSlide(index, true);
    }

    handleSectionUnload(event) {
      if (String(event.detail?.sectionId) === this.dataset.sectionId) this.remove();
    }

    handlePageShow(event) {
      if (event.persisted && this.isOpen) this.finishClose(false);
    }

    open(opener) {
      if (this.isOpen || this.hasCompetingOverlay()) return false;

      this.opener = opener instanceof HTMLElement ? opener : document.activeElement;
      const activeBlockId = this.gallery?.querySelector('[data-dio-mode-panel].is-active')?.dataset.dioBlockId;
      const openingIndex = this.slides.findIndex((slide) => slide.dataset.dioBlockId === activeBlockId);
      this.setSlide(openingIndex >= 0 ? openingIndex : 0);

      this.hidden = false;
      this.dialog.scrollTop = 0;
      this.setAttribute('aria-hidden', 'false');
      this.dataset.state = 'opening';
      this.isOpen = true;
      this.inertBackground();
      this.addedBodyLock = !document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden', 'threedi-mode-lightbox-open');

      window.requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.dataset.state = 'open';
        const closeButton = this.querySelector('button[data-dio-lightbox-close]');
        if (typeof trapFocus === 'function') trapFocus(this.dialog, closeButton || this.dialog);
        else this.dialog.focus();
      });

      if (this.autoplay && this.slides.length > 1 && !this.reducedMotion.matches) this.startPlayback();
      else this.pausePlayback();
      return true;
    }

    close() {
      if (!this.isOpen || this.dataset.state === 'closing') return;
      this.pausePlayback();
      this.dataset.state = 'closing';
      const duration = this.reducedMotion.matches ? 0 : 180;
      this.clearCloseTimer();
      this.closeTimer = window.setTimeout(() => this.finishClose(true), duration);
    }

    finishClose(restoreFocus) {
      this.clearTimer();
      this.clearCloseTimer();
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      delete this.dataset.state;
      this.isOpen = false;
      this.playing = false;
      this.restoreBackground();
      document.body.classList.remove('threedi-mode-lightbox-open');
      if (this.addedBodyLock) document.body.classList.remove('overflow-hidden');
      this.addedBodyLock = false;

      const focusTarget = restoreFocus && this.opener?.isConnected ? this.opener : null;
      if (typeof removeTrapFocus === 'function') removeTrapFocus(focusTarget);
      else focusTarget?.focus();
      this.updateControls();
    }

    move(direction) {
      if (this.slides.length < 2) return;
      let nextIndex = this.activeIndex + direction;
      if (this.loop) nextIndex = (nextIndex + this.slides.length) % this.slides.length;
      else nextIndex = Math.max(0, Math.min(this.slides.length - 1, nextIndex));
      if (nextIndex === this.activeIndex) return;
      this.setSlide(nextIndex, true);
    }

    setSlide(index, manual = false) {
      if (index < 0 || index >= this.slides.length) return;
      this.activeIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });

      const activeBlockId = this.slides[index].dataset.dioBlockId;
      let activeCaption = null;
      this.captions.forEach((caption) => {
        const active = caption.dataset.dioBlockId === activeBlockId;
        caption.hidden = !active;
        if (active) activeCaption = caption;
      });
      this.details?.classList.toggle('is-empty', !activeCaption?.textContent.trim());

      if (this.currentOutput) this.currentOutput.textContent = String(index + 1);
      this.updateControls();

      if (manual && this.playing) this.scheduleNext();
    }

    startPlayback() {
      if (!this.isOpen || this.slides.length < 2) return;
      if (!this.loop && this.activeIndex === this.slides.length - 1) this.setSlide(0);
      this.playing = true;
      this.updateControls();
      this.scheduleNext();
    }

    pausePlayback() {
      this.playing = false;
      this.clearTimer();
      this.updateControls();
    }

    scheduleNext() {
      this.clearTimer();
      if (!this.isOpen || !this.playing || document.hidden || this.slides.length < 2) return;
      const interval = Number(this.dataset.interval) || 3000;
      this.timer = window.setTimeout(() => {
        if (!this.loop && this.activeIndex === this.slides.length - 1) {
          this.pausePlayback();
          return;
        }
        const nextIndex = this.loop ? (this.activeIndex + 1) % this.slides.length : this.activeIndex + 1;
        this.setSlide(nextIndex);
        this.scheduleNext();
      }, interval);
    }

    updateControls() {
      const multipleSlides = this.slides.length > 1;
      if (this.controls) this.controls.hidden = !multipleSlides;
      if (this.previousButton) this.previousButton.disabled = !multipleSlides || (!this.loop && this.activeIndex === 0);
      if (this.nextButton) {
        this.nextButton.disabled = !multipleSlides || (!this.loop && this.activeIndex === this.slides.length - 1);
      }
      if (!this.playbackButton) return;

      const label = this.playing ? 'Pause animation' : 'Play animation';
      this.playbackButton.disabled = !multipleSlides;
      this.playbackButton.setAttribute('aria-label', label);
      this.playbackButton.title = label;
      if (this.playIcon) this.playIcon.hidden = this.playing;
      if (this.pauseIcon) this.pauseIcon.hidden = !this.playing;
    }

    hasCompetingOverlay() {
      if (document.body.classList.contains('overflow-hidden')) return true;
      return Boolean(
        document.querySelector(
          'modal-dialog[open], product-modal[open], cart-drawer.active, cart-notification.active, details-modal details[open], menu-drawer details[open], header-drawer details[open], three-dio-popup:not([hidden]), three-dio-mode-lightbox[data-state="open"]'
        )
      );
    }

    inertBackground() {
      this.inertedElements = [...document.body.children]
        .filter((element) => element !== this && !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName))
        .map((element) => ({ element, wasInert: element.inert }));
      this.inertedElements.forEach(({ element }) => {
        element.inert = true;
      });
    }

    restoreBackground() {
      this.inertedElements.forEach(({ element, wasInert }) => {
        element.inert = wasInert;
      });
      this.inertedElements = [];
    }

    clearTimer() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    clearCloseTimer() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  customElements.define('three-dio-mode-lightbox', ThreeDioModeLightbox);
})();
